import React, { useEffect, useContext, useMemo, useState, FC, isValidElement } from 'react';
import { useFormField, formContext, usePrevious } from './hooks';
import { removeField } from './state';

export interface FieldProps {
    name: string;
    type?: string;
    value: string;
    displayHint?: (arg: string) => string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    validate?: (value: string) => Array<string>;
    removable?: boolean;
    foundValue?: string;
    readOnlyValue?: string;
}

export interface FieldRenderProps {
    name: string;
    value: string;
    errors: Array<string>;
    touched: boolean;
    disabled: boolean;
    readOnly: boolean;
    onChange: (value: string | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    onBlur: (value: string) => void;
    hint?: string;
    showErrorNotification?: boolean;
}

type OverrideValueState = string | undefined;

export const Field: FC<FieldProps> = ({
    name,
    onChange: onChangeParent,
    validate,
    children,
    disabled,
    value: initialValue,
    removable,
    displayHint,
    foundValue,
    readOnlyValue
}) => {
    const [overrideValue, setOverrideValue] = useState<OverrideValueState>(readOnlyValue || foundValue || undefined);
    const [field, setField] = useFormField(name, readOnlyValue || initialValue);
    const [countryField] = useFormField('country');
    const { value: formCountry } = countryField;
    const prevCountry = usePrevious(countryField);
    const { touched: formTouched, dispatch } = useContext(formContext);
    const { value, errors, touched: fieldTouched, hint, showErrorNotification } = field;
    const touched = formTouched || fieldTouched;

    // Set initial errors on mount so the form can't be prematurely submitted
    const prevTouched = usePrevious(touched);
    const prevOverrideValue = usePrevious(overrideValue);

    useEffect(() => {
        if (overrideValue !== (readOnlyValue || foundValue)) {
            setOverrideValue(readOnlyValue || foundValue || undefined)
        }
    }, [readOnlyValue, foundValue])

    useEffect(() => {
        if (typeof overrideValue != 'undefined' || (typeof prevOverrideValue !== 'undefined' && !overrideValue)) {
            const newValue = overrideValue ? overrideValue : initialValue || '';
            const errors = overrideValue && validate ? validate(String(newValue)) : field.errors;
            const hint = overrideValue && displayHint ? displayHint(overrideValue) : '';
            setField({
                value: newValue,
                errors,
                hint,
                touched: overrideValue && overrideValue != '' ? true : false,
            });
        }
    }, [overrideValue])

    useEffect(() => {
        if (typeof prevTouched == 'undefined' && (initialValue || validate)) {
            if (typeof readOnlyValue != 'undefined' && field.value !== readOnlyValue) {
                setField({
                    value: readOnlyValue,
                    errors: [],
                    hint: undefined,
                });
            } else {
                const value = initialValue ? initialValue : field.value;
                const errors = validate ? validate(String(value)) : field.errors;
                const hint = displayHint ? displayHint(value) : '';
                if (!initialValue || field.value === initialValue) {
                    setField({
                        value,
                        errors,
                        hint,
                    });
                }
            }
        } else if (prevTouched === false && touched === true) {
            setField({ touched });
        }
    }, [touched, foundValue, readOnlyValue]);

    useEffect(() => {
        return () => {
            if (removable) {
                const arrayAccessRegex = /(\w+)\[(\d+)\]$/;
                const match = arrayAccessRegex.exec(name);
                const parsedName = match ? match[1] : name;
                dispatch(removeField(parsedName));
            }
        };
    }, []);

    useEffect(() => {
        if (typeof prevCountry != 'undefined' && prevCountry.value !== formCountry && name != 'country' && name != 'addressId') {
            const newValue = name === 'addressType' ? initialValue : overrideValue || '';
            const errors = validate ? validate(String(newValue)) : field.errors;

            setField({
                ...field,
                touched: undefined,
                errors,
                value: newValue,
            });
        }
    }, [formCountry, field]);

    return useMemo(() => {
        const onChange = (value: string | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            if (typeof value == 'string') {
                const fieldErrors = validate ? validate(value) || [] : [];
                const hint = displayHint ? displayHint(value) : '';

                setField({ value, errors: fieldErrors, touched: true, hint, showErrorNotification: !!field.touched && !!field.showErrorNotification });
                onChangeParent && onChangeParent(value);
            } else {
                const fieldErrors = validate ? validate(value.target.value) || [] : [];
                setField({ value: value.target.value, errors: fieldErrors, touched: true });
                onChangeParent && onChangeParent(value.target.value);
            }
        };

        const onBlur = () => {
            if (validate) {
                // const value = ev.target ? ev.target.value : ev.currentTarget ? ev.currentTarget.value : ev
                // const errors = validate(value) || []
                setField({ touched: true, showErrorNotification: true });
            }
        };

        if (typeof children == 'function') {
            return children({ name, value, errors, touched, onChange, onBlur, disabled, readOnly: Boolean(readOnlyValue), hint, showErrorNotification });
        }
        if (children && isValidElement(children)) {
            // @ts-ignore
            return React.cloneElement(children, { name, value, errors, touched, onChange, onBlur, disabled, readOnly: Boolean(readOnlyValue), hint });
        }
        return null;
    }, [value, errors, disabled, formTouched, touched, fieldTouched, validate, name, onChangeParent, readOnlyValue, initialValue, showErrorNotification, children]);
};
